/**
 * @fileoverview consoleSessions Pinia store
 *
 * Owns the lifetime of multiple Guacamole console sessions so they survive
 * route changes and can be switched between via the global dock / Switch VM.
 * Lab sessions use map keys equal to `vmId`. Share portal sessions use keys
 * `share:<shareId>:<vmId>` so the same VM id cannot collide across contexts.
 *
 * @see apps/console/src/composables/consoleSessionController.js
 */

import { defineStore } from 'pinia'
import { computed, markRaw, reactive, ref } from 'vue'
import { createConsoleSessionController } from '@/composables/consoleSessionController'
import { useNotificationsStore } from '@/stores/notifications'
import { getShareSessionToken } from '@/api/client'

export const MAX_SESSIONS = 4
export const IDLE_TTL_MS = 60 * 60 * 1000
export const IDLE_WARN_BEFORE_MS = 5 * 60 * 1000

/** @param {string} shareId @param {string} vmId */
export function shareConsoleSessionKey(shareId, vmId) {
  return `share:${shareId}:${vmId}`
}

/**
 * @typedef {Object} ConsoleSession
 * @property {string} sessionKey - Map key (`vmId` for lab, `share:…` for share)
 * @property {string} vmId
 * @property {string|null} shareId - Set for share portal sessions
 * @property {string} labId - Lab id for lab console; `share:${shareId}` for share
 * @property {string} labName
 * @property {string} vmName
 * @property {ReturnType<typeof createConsoleSessionController>} controller
 * @property {HTMLElement|null} displayElement
 * @property {number} openedAt
 * @property {number} lastFocusedAt
 * @property {number|null} idleTimerId
 * @property {number|null} idleWarningTimerId
 * @property {number|null} idleDeadline
 */

export const useConsoleSessionsStore = defineStore('consoleSessions', () => {
  /** @type {Map<string, ConsoleSession>} */
  const sessions = reactive(new Map())
  /** Active session map key (lab: vmId, share: `share:<shareId>:<vmId>`) */
  const activeVmId = ref(null)
  const isHostVisible = ref(false)

  let beforeUnloadAttached = false

  function _attachBeforeUnload() {
    if (beforeUnloadAttached) return
    if (typeof window === 'undefined') return
    window.addEventListener('beforeunload', closeAll)
    beforeUnloadAttached = true
  }

  function _clearIdleTimers(session) {
    if (session.idleTimerId !== null) {
      clearTimeout(session.idleTimerId)
      session.idleTimerId = null
    }
    if (session.idleWarningTimerId !== null) {
      clearTimeout(session.idleWarningTimerId)
      session.idleWarningTimerId = null
    }
    session.idleDeadline = null
  }

  function _scheduleIdleTimers(session) {
    _clearIdleTimers(session)
    const notifications = useNotificationsStore()
    session.idleDeadline = Date.now() + IDLE_TTL_MS
    session.idleWarningTimerId = setTimeout(() => {
      notifications.warning(
        `${session.vmName || 'A console session'} will close in ${IDLE_WARN_BEFORE_MS / 60000} minutes due to inactivity. Re-open it to keep the session alive.`,
        'Console session idle'
      )
    }, IDLE_TTL_MS - IDLE_WARN_BEFORE_MS)
    session.idleTimerId = setTimeout(() => {
      notifications.info(
        `${session.vmName || 'A console session'} was closed after 1 hour of inactivity.`,
        'Console session closed'
      )
      closeSession(session.sessionKey)
    }, IDLE_TTL_MS)
  }

  /**
   * @param {{labId: string, vmId: string, vmName?: string, labName?: string}} payload
   * @returns {{ok: true, session: ConsoleSession} | {ok: false, reason: string}}
   */
  function openSession({ labId, vmId, vmName = '', labName = '' }) {
    if (!vmId || !labId) {
      return { ok: false, reason: 'labId and vmId are required' }
    }

    const sessionKey = vmId

    if (sessions.has(sessionKey)) {
      const existing = sessions.get(sessionKey)
      if (vmName && !existing.vmName) existing.vmName = vmName
      if (labName && !existing.labName) existing.labName = labName
      focusSession(sessionKey)
      return { ok: true, session: existing }
    }

    if (sessions.size >= MAX_SESSIONS) {
      const notifications = useNotificationsStore()
      notifications.warning(
        `You have reached the maximum of ${MAX_SESSIONS} concurrent console sessions. Close one before opening another.`,
        'Console session limit'
      )
      return { ok: false, reason: 'session limit reached' }
    }

    const controller = markRaw(createConsoleSessionController())
    /** @type {ConsoleSession} */
    const session = {
      sessionKey,
      vmId,
      shareId: null,
      labId,
      vmName,
      labName,
      controller,
      displayElement: null,
      openedAt: Date.now(),
      lastFocusedAt: Date.now(),
      idleTimerId: null,
      idleWarningTimerId: null,
      idleDeadline: null
    }
    sessions.set(sessionKey, session)
    _attachBeforeUnload()
    focusSession(sessionKey)
    return { ok: true, session }
  }

  /**
   * @param {{shareId: string, vmId: string, vmName?: string, labName?: string}} payload
   * @returns {{ok: true, session: ConsoleSession} | {ok: false, reason: string}}
   */
  function openShareSession({ shareId, vmId, vmName = '', labName = '' }) {
    if (!vmId || !shareId) {
      return { ok: false, reason: 'shareId and vmId are required' }
    }

    const sessionKey = shareConsoleSessionKey(shareId, vmId)

    if (sessions.has(sessionKey)) {
      const existing = sessions.get(sessionKey)
      if (vmName && !existing.vmName) existing.vmName = vmName
      if (labName && !existing.labName) existing.labName = labName
      focusSession(sessionKey)
      return { ok: true, session: existing }
    }

    if (sessions.size >= MAX_SESSIONS) {
      const notifications = useNotificationsStore()
      notifications.warning(
        `You have reached the maximum of ${MAX_SESSIONS} concurrent console sessions. Close one before opening another.`,
        'Console session limit'
      )
      return { ok: false, reason: 'session limit reached' }
    }

    const controller = markRaw(createConsoleSessionController())
    const labIdScoped = `share:${shareId}`
    /** @type {ConsoleSession} */
    const session = {
      sessionKey,
      vmId,
      shareId,
      labId: labIdScoped,
      vmName,
      labName,
      controller,
      displayElement: null,
      openedAt: Date.now(),
      lastFocusedAt: Date.now(),
      idleTimerId: null,
      idleWarningTimerId: null,
      idleDeadline: null
    }
    sessions.set(sessionKey, session)
    _attachBeforeUnload()
    focusSession(sessionKey)
    return { ok: true, session }
  }

  /**
   * @param {string} sessionKey
   */
  function focusSession(sessionKey) {
    const session = sessions.get(sessionKey)
    if (!session) return
    _clearIdleTimers(session)
    session.lastFocusedAt = Date.now()
    activeVmId.value = sessionKey
    isHostVisible.value = true
    for (const s of sessions.values()) {
      s.controller.setKeyboardEnabled(s.sessionKey === sessionKey)
    }
  }

  /**
   * @param {string} sessionKey
   */
  function minimizeSession(sessionKey) {
    const session = sessions.get(sessionKey)
    if (!session) return
    if (activeVmId.value === sessionKey) {
      activeVmId.value = null
      isHostVisible.value = false
    }
    session.controller.setKeyboardEnabled(false)
    _scheduleIdleTimers(session)
  }

  /**
   * @param {string} sessionKey
   */
  function closeSession(sessionKey) {
    const session = sessions.get(sessionKey)
    if (!session) return
    _clearIdleTimers(session)
    try {
      session.controller.disconnect(false)
    } catch (err) {
      console.warn('[consoleSessions] disconnect threw during close:', err)
    }
    sessions.delete(sessionKey)
    if (activeVmId.value === sessionKey) {
      activeVmId.value = null
      isHostVisible.value = false
    }
  }

  function closeAll() {
    for (const sessionKey of Array.from(sessions.keys())) {
      closeSession(sessionKey)
    }
  }

  /**
   * @param {string} sessionKey
   * @param {HTMLElement} displayElement
   */
  function registerDisplay(sessionKey, displayElement) {
    const session = sessions.get(sessionKey)
    if (!session) return
    if (session.displayElement === displayElement) return
    session.displayElement = displayElement
    session.controller.attach(displayElement)
  }

  /**
   * @param {string} sessionKey
   */
  async function connectSession(sessionKey) {
    const session = sessions.get(sessionKey)
    if (!session) return
    if (session.shareId) {
      const token = getShareSessionToken()
      if (!token) {
        throw new Error('No share session token. Sign in again.')
      }
      await session.controller.connectShare(session.shareId, session.vmId, token)
    } else {
      await session.controller.connect(session.vmId)
    }
  }

  const sessionList = computed(() => Array.from(sessions.values()))
  const activeSession = computed(() =>
    activeVmId.value ? sessions.get(activeVmId.value) || null : null
  )

  function sessionsForLab(labId) {
    return sessionList.value.filter(s => s.labId === labId)
  }

  return {
    sessions,
    sessionList,
    activeVmId,
    activeSession,
    isHostVisible,
    openSession,
    openShareSession,
    focusSession,
    minimizeSession,
    closeSession,
    closeAll,
    registerDisplay,
    connectSession,
    sessionsForLab
  }
})
